import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';

import * as API from 'es6/core/api';
import AggregateMemory from 'es6/models/aggregate-memory';
import Config from 'es6/core/config';
import CourseContent from 'es6/containers/courses/course-content';
import CourseSummary from 'es6/components/courses/course-summary';
import Goal from 'es6/models/goal';
import Logger from 'es6/services/logger';
import { t } from 'es6/services/localization';
import Memory from 'es6/models/memory';
import { schedule as scheduleMouseoverDictionaryLoad } from 'iknow-mouseover-dictionary/loader';
import { noticeError } from 'es6/core/errors';
import SiteController from 'es6/controllers/site-controller';

// Success messages
const AddedToCourseSuccess = t('items added to goal');

// Error messages
const EnrollError = t('growl-message.goal-enroll-error');
const FocusError = t('growl-message.goal-focus-error');
const UnenrollError = t('growl-message.goal-unenroll-error');
const UnfocusError = t('growl-message.goal-unfocus-error');
const UnexpectedError = t('growl-message.unexpected-error');

export default class Courses extends SiteController {
  /**
   * @param {Number} goalId
   */
  constructor(store, goalId, ...args) {
    super(store, ...args);

    this._aggregateMemory = null;
    this._courseContentContainer = document.querySelector('.course-content-container');
    this._courseSummaryContainer = document.querySelector('.course-summary-container');
    this._customGoals = null;
    this._goal = null;
    this._goalId = goalId;
    this._memories = new Map();
    const mouseoverDictionaryPromise = scheduleMouseoverDictionaryLoad({ courses: [goalId] });

    // Because getUserCreatedGoals throws a 500 if the user is not logged in,
    // create a fallback to return an empty array.
    const getUserCreatedGoals = Config.isLoggedIn() ?
      API.getUserCreatedGoals() :
      new Promise(resolve => resolve([]));

    // Retrieve aggregates, memories, goal and custom courses.
    Promise.all([
      API.getGoal(goalId),
      getUserCreatedGoals,
      // Wait for it to load but ignore loading errors
      mouseoverDictionaryPromise.catch(error => error),
      this._fetchMemoryData(),
    ]).then(responses => {
      this._goal = new Goal(responses[0]);

      // Create custom goals, but filter out the ones that don't match
      // the same cue and response language as the goal.
      this._customGoals = responses[1].map(goal => new Goal(goal)).filter(goal =>
        goal.cueLanguage === this._goal.cueLanguage && goal.responseLanguage === this._goal.responseLanguage
      );

      this._renderComponents();
    }).catch(reason => {
      Logger.error(reason);
      noticeError(reason);
    });
  }

  /**
   * Retrieves the data and sets up the basic class variables.
   *
   * @returns {Promise}
   */
  _fetchMemoryData() {
    const goalId = this._goalId;

    // If the user is not logged in, just resolve. Memory APIs
    // always require authentication.
    if (!Config.isLoggedIn()) {
      return Promise.resolve(true);
    }

    // Retrieve goal and aggregate memory, then render the components.
    return Promise.all([
      API.getAggregateMemories(goalId),
      API.getGoalMemories(goalId),
    ])

    // Set the goal and aggregate memory data. Then render the components.
      .then(responses => {
        this._aggregateMemory = new AggregateMemory(responses[0]);
        this._memories = new Map();

      // Add memories to the memories map.
        responses[1].forEach(memory => {
          if (memory.contentDomain === 'items') {
            this._memories.set(memory.contentId, new Memory(memory));
          }
        });
      });
  }

  /**
   * Helper method for catching exceptions with the course APIs.
   * Simply re-render the course summary without updating anything
   * and pass the error to New Relic.
   *
   * @param {String} growlMessage
   * @param {Error} reason
   */
  _handleError(growlMessage, reason, renderCourseSummary = false, renderCourseContent = false) {
    $.growl.error(growlMessage);

    if (renderCourseSummary) {
      this._renderCourseSummary();
    }

    if (renderCourseContent) {
      this._renderCourseContent();
    }

    noticeError(reason);
  }

  _handleEnroll() {
    this._handleGoalCallback(API.postEnrollGoal, EnrollError, { focus: false });
  }

  _handleFocus() {
    this._handleGoalCallback(API.postFocusGoal, FocusError);
  }

  _handleUnenroll() {
    this._handleGoalCallback(API.postUnenrollGoal, UnenrollError);
  }

  _handleUnfocus() {
    this._handleGoalCallback(API.postUnfocusGoal, UnfocusError);
  }

  /**
   * Helper method for goal callback actions.
   *
   * @param {Function} apiCallback
   * @param {String} errorMessage    error message to show on fail
   * @param {Object} params          additional parameters to pass in
   */
  _handleGoalCallback(apiCallback, errorMessage, params = {}) {
    this._renderCourseSummary(true);

    return apiCallback(this._goal.id, params)
    .then(() => this._fetchMemoryData())
    .then(() => this._renderCourseSummary())
    .catch(this._handleError.bind(this, errorMessage, true, false));
  }

  /**
   * Callback for the AddToCoursesModal onSubmit call.
   *
   * @param {Number[]} itemIds
   * @param {Number} customGoalId
   */
  _handleCopyToCustomCourse(itemIds, customGoalId) {
    this._renderCourseContent(true);

    const items = itemIds.map(itemId => (
      { goalId: this._goal.id, itemId }
    ));

    API.postCopyItemsToCustomGoal(customGoalId, { items }).then(() => {
      // If successful, show the growl and remove the pulsating effect
      // on the content listing box.
      $.growl.notice(AddedToCourseSuccess);
      this._renderCourseContent();
    }).catch(this._handleError.bind(this, UnexpectedError, false, true));
  }

  /**
   * Handles setting items as completed.
   *
   * @param {Number[]} itemIds
   */
  _handleSetToComplete(itemIds) {
    this._handleBulkItemCallbackHelper(itemIds, API.postMemoryCheckpoint);
  }

  /**
   * Handles resetting items.
   *
   * @param {Number[]} itemIds
   */
  _handleReset(itemIds) {
    this._handleBulkItemCallbackHelper(itemIds, API.postResetItems);
  }

  /**
   * Handles skipping items.
   *
   * @param {Number[]} itemIds
   */
  _handleSkip(itemIds) {
    this._handleBulkItemCallbackHelper(itemIds, API.postSkipItems);
  }

  /**
   * Handles skipping items.
   *
   * @param {Number[]} itemIds
   */
  _handleUnskip(itemIds) {
    this._handleBulkItemCallbackHelper(itemIds, API.postUnskipItems);
  }

  /**
   * Helper method for some of the bulk action callbacks. Will
   * fetch data when the action is complete and re-render the
   * course and course content component.
   *
   * @param {Number[]} itemIds
   * @param {Function} apiCallback
   * @returns {Promise}
   */
  _handleBulkItemCallbackHelper(itemIds, apiCallback) {
    // Don't do anything if there are no item IDs.
    if (itemIds.length === 0) {
      return undefined;
    }

    this._renderCourseContent(true);

    return apiCallback(itemIds)
    .then(() => this._fetchMemoryData())
    .then(() => {
      this._renderCourseSummary();
      this._renderCourseContent();
    })
    .catch(this._handleError.bind(this, UnexpectedError, true, true));
  }

  /**
   * Render all the react components on the page.
   */
  _renderComponents() {
    this._renderCourseContent();
    this._renderCourseSummary();
  }

  /**
   * Helper method to rerender the course content with updated
   * item packages and memories.
   *
   * @param {Boolean} isLoading     gets passed into the component
   */
  _renderCourseContent(isLoading = false) {
    const courseContentProps = {
      enableBulkActions: Config.isLoggedIn(),
      itemPackages: this._goal.goalItems,
      isLoading,
      memories: this._memories,
      onAddToCourse: (...args) => this._handleCopyToCustomCourse(...args),
      customGoals: this._customGoals,
      onReset: this._handleReset.bind(this),
      onSetToComplete: this._handleSetToComplete.bind(this),
      onSkip: this._handleSkip.bind(this),
      onUnskip: this._handleUnskip.bind(this),
    };

    ReactDOM.render(
      <Provider store={this.store}>
        <CourseContent {...courseContentProps} />
      </Provider>,
      this._courseContentContainer
    );
  }

  /**
   * Helper method to rerender the course summary with updated
   * goal and/or aggregate data.
   *
   * @param {Boolean} isLoading     gets passed into the component
   */
  _renderCourseSummary(isLoading = false) {
    const courseSummaryProps = {
      aggregateMemory: this._aggregateMemory,
      goal: this._goal,
      enableCourseActions: Config.isLoggedIn(),
      enableCourseLauncher: Config.isLoggedIn(),
      enableCourseProgress: Config.isLoggedIn(),
      isLoading,
      onEnroll: this._handleEnroll.bind(this),
      onFocus: this._handleFocus.bind(this),
      onUnenroll: this._handleUnenroll.bind(this),
      onUnfocus: this._handleUnfocus.bind(this),
    };

    ReactDOM.render(<CourseSummary {...courseSummaryProps} />, this._courseSummaryContainer);
  }

  remove() {
    ReactDOM.unmountComponentAtNode(this._courseSummaryContainer);
    ReactDOM.unmountComponentAtNode(this._courseContentContainer);
  }
}
